import { Observable, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import { sharedConstants } from '../shared-constants';
import { Injectable } from '@angular/core';
import * as UrlService from './url.service';
import { Facility } from '../models/facility';

export class FacilitySearchParms {
  Page: number;
  PageSize: number;
  FacilitySearch: string;
  DistrictSearch: string;
  AreaSearch: string;
  RegionSearch: string;
  FacilityId: string;
  DistrictId: string;
  AreaId: string;
  RegionId: string;
  LimitByRegionId: string;
  LimitByAreaId: string;
  LimitByDistrictId: string;

  GetKey() {
    if (this.FacilityId) {
      return `FacilityId: ${this.FacilityId}`;
    } else if (this.DistrictId) {
      return `DistrictId: ${this.DistrictId}`;
    } else if (this.AreaId) {
      return `AreaId: ${this.AreaId}`;
    } else if (this.RegionId) {
      return `RegionId: ${this.RegionId}`;
    } else {
      return `Search: ${(this.Page ? this.Page : 1)},${(this.PageSize ? this.PageSize : 20)},${(this.FacilitySearch ? this.FacilitySearch : '')},${(this.DistrictSearch ? this.DistrictSearch : '')},${(this.AreaSearch ? this.AreaSearch : '')},${(this.RegionSearch ? this.RegionSearch : '')},${this.LimitByRegionId ? this.LimitByRegionId : ''},${this.LimitByAreaId ? this.LimitByAreaId : ''},${this.LimitByDistrictId ? this.LimitByDistrictId : ''}`
    }
  }
}

class CachedItem<T> {
  private _lastUsed: Date;
  constructor(public Value: T) {
    this._lastUsed = new Date();
  }

  public SetUsed(): void {
    this._lastUsed = new Date();
  }

  public LastUsed(): Date {
    return this._lastUsed;
  }
}

@Injectable()
export class FacilitySelectorService {

  private readonly CACHE_LIMIT: number = 50;
  private controllerName = "facilityselector";
  private cache: { [key: string]: CachedItem<Observable<Facility[]>> } = {};

  constructor(
    private apiClient: UrlService.UrlService
  ) { }

  public getFacilities(facilitySelectionType: string, locationLevel: string, searchParms: FacilitySearchParms): Observable<Facility[]> {
    //Todo: Need to clear cache automatically.  Keep 50.

    const key = `${facilitySelectionType},${locationLevel},${searchParms.GetKey()}`
    if (!this.cache[key])
      this.addCachedItem(key, this.fetchFacilities(facilitySelectionType, locationLevel, searchParms).pipe(shareReplay(1)));

    return this.getCachedItem(key);
  }
  private addCachedItem(key: string, value: Observable<Facility[]>) {
    this.cache[key] = new CachedItem(value);
    this.cleanupCache();
  }

  private getCachedItem(key: string): Observable<Facility[]> {
    this.cache[key].SetUsed();
    return this.cache[key].Value;
  }

  private cleanupCache() {
    while (Object.keys(this.cache).length > this.CACHE_LIMIT) {
      let oldestKey: string;
      let oldestTime: Date;

      for (const key in this.cache) {
        const item = this.cache[key];

        if (!oldestKey) {
          oldestKey = key;
          oldestTime = item.LastUsed();
        } else if (oldestTime > item.LastUsed()) {
          oldestKey = key;
          oldestTime = item.LastUsed();
        }
      }

      delete this.cache[oldestKey];
    }
  }

  private fetchFacilities(facilitySelectionType: string, locationLevel: string, searchParms: FacilitySearchParms): Observable<Facility[]> {
    const query = new UrlService.OdataQuery();

    if (searchParms.FacilityId) {
      //Match on Facility Id
      const filterGroup = [] as UrlService.IOdataFilter[];

      filterGroup.push(
        new UrlService.OdataFilterCompare(
          new UrlService.OdataFieldName("FacilityNumber"),
          new UrlService.OdataFieldString(searchParms.FacilityId),
          UrlService.OdataFilterCompareType.Equal
        )
      );

      query.setFilter(
        filterGroup[0]
      );
    } else if (searchParms.DistrictId) {
      //Match on District Id
      const filterGroup = [] as UrlService.IOdataFilter[];

      filterGroup.push(
        new UrlService.OdataFilterCompare(
          new UrlService.OdataFieldName("DistrictNumber"),
          new UrlService.OdataFieldString(searchParms.DistrictId),
          UrlService.OdataFilterCompareType.Equal
        )
      );

      query.setFilter(
        filterGroup[0]
      );
    } else if (searchParms.AreaId) {
      //Match on Area Id
      const filterGroup = [] as UrlService.IOdataFilter[];

      filterGroup.push(
        new UrlService.OdataFilterCompare(
          new UrlService.OdataFieldName("AreaNumber"),
          new UrlService.OdataFieldString(searchParms.AreaId),
          UrlService.OdataFilterCompareType.Equal
        )
      );

      query.setFilter(
        filterGroup[0]
      );
    } else if (searchParms.RegionId) {
      //Match on Region Id
      const filterGroup = [] as UrlService.IOdataFilter[];

      filterGroup.push(
        new UrlService.OdataFilterCompare(
          new UrlService.OdataFieldName("RegionNumber"),
          new UrlService.OdataFieldString(searchParms.RegionId),
          UrlService.OdataFilterCompareType.Equal
        )
      );

      query.setFilter(
        filterGroup[0]
      );
    } else {
      //Search
      query
        //.setCount(true)
        .setPage(!searchParms.Page ? 0 : searchParms.Page)
        .setPageSize(!searchParms.PageSize ? 20 : searchParms.PageSize);

      let limitByFilterGroup = [] as UrlService.IOdataFilter[];

      if (searchParms.LimitByRegionId)
        limitByFilterGroup.push(
          new UrlService.OdataFilterCompare(
            new UrlService.OdataFieldName("RegionNumber"),
            new UrlService.OdataFieldString(searchParms.LimitByRegionId),
            UrlService.OdataFilterCompareType.Equal
          )
        )

      if (searchParms.LimitByAreaId)
        limitByFilterGroup.push(
          new UrlService.OdataFilterCompare(
            new UrlService.OdataFieldName("AreaNumber"),
            new UrlService.OdataFieldString(searchParms.LimitByAreaId),
            UrlService.OdataFilterCompareType.Equal
          )
        )

      if (searchParms.LimitByDistrictId)
        limitByFilterGroup.push(
          new UrlService.OdataFilterCompare(
            new UrlService.OdataFieldName("DistrictNumber"),
            new UrlService.OdataFieldString(searchParms.LimitByDistrictId),
            UrlService.OdataFilterCompareType.Equal
          )
        )

      if (limitByFilterGroup.length > 1) {
        const tempFilterGroup = [] as UrlService.IOdataFilter[];
        tempFilterGroup.push(
          new UrlService.OdataFilterGroup(
            limitByFilterGroup,
            UrlService.OdataFilterGroupType.And
          )
        );
        limitByFilterGroup = tempFilterGroup;
      }

      let searchFilterGroup = [] as UrlService.IOdataFilter[];

      if (searchParms.FacilitySearch)
        searchFilterGroup.push(
          new UrlService.OdataFilterSubstringOf(
            new UrlService.OdataFieldToLower(new UrlService.OdataFieldName("FacilityName")),
            new UrlService.OdataFieldToLower(new UrlService.OdataFieldString(searchParms.FacilitySearch))
          )
        );

      if (searchParms.DistrictSearch)
        searchFilterGroup.push(
          new UrlService.OdataFilterSubstringOf(
            new UrlService.OdataFieldToLower(new UrlService.OdataFieldName("DistrictName")),
            new UrlService.OdataFieldToLower(new UrlService.OdataFieldString(searchParms.DistrictSearch))
          )
        );

      if (searchParms.AreaSearch)
        searchFilterGroup.push(
          new UrlService.OdataFilterSubstringOf(
            new UrlService.OdataFieldToLower(new UrlService.OdataFieldName("AreaName")),
            new UrlService.OdataFieldToLower(new UrlService.OdataFieldString(searchParms.AreaSearch))
          )
        );

      if (searchParms.RegionSearch)
        searchFilterGroup.push(
          new UrlService.OdataFilterSubstringOf(
            new UrlService.OdataFieldToLower(new UrlService.OdataFieldName("RegionName")),
            new UrlService.OdataFieldToLower(new UrlService.OdataFieldString(searchParms.RegionSearch))
          )
        );

      if (searchFilterGroup.length > 1) {
        const tempFilterGroup = [] as UrlService.IOdataFilter[];
        tempFilterGroup.push(
          new UrlService.OdataFilterGroup(
            searchFilterGroup,
            UrlService.OdataFilterGroupType.Or
          )
        );
        searchFilterGroup = tempFilterGroup;
      }

      if (searchFilterGroup.length > 0 && limitByFilterGroup.length > 0) {
        const tempFilterGroup = [] as UrlService.IOdataFilter[];
        tempFilterGroup.push(
          new UrlService.OdataFilterGroup(
            [
              searchFilterGroup[0],
              limitByFilterGroup[0]
            ],
            UrlService.OdataFilterGroupType.And
          )
        );
        query.setFilter(tempFilterGroup[0]);
      } else if (limitByFilterGroup.length > 0) {
        query.setFilter(limitByFilterGroup[0]);
      } else if (searchFilterGroup.length > 0) {
        query.setFilter(searchFilterGroup[0]);
      }
    }

    return this.apiClient.get(sharedConstants.appName, this.controllerName, null, null,
      new UrlService.QueryOptions()
        .addQueryParameter(
          new UrlService.QueryParameter("facilitySelectionType", facilitySelectionType),
      )
        .addQueryParameter(
          new UrlService.QueryParameter("locationLevel", locationLevel),
        )
        .setOdataQuery(
          query
        )
    );
  }
}
